class CustomSubscription extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  /**
   * Helper: get the currently active storefront currency code.
   * Falls back to shop currency or GBP if nothing else is available.
   */
  getCurrentCurrencyCode() {
    if (window.Shopify) {
      if (Shopify.currency && Shopify.currency.active) {
        return Shopify.currency.active;
      }
      if (Shopify.currency && Shopify.currency.shopCurrency) {
        return Shopify.currency.shopCurrency;
      }
    }

    const htmlEl = document.documentElement;
    const shopCurrencyAttr =
      htmlEl.getAttribute('data-shop-currency') ||
      htmlEl.getAttribute('data-money-format-currency');

    if (shopCurrencyAttr) {
      return shopCurrencyAttr;
    }

    return 'GBP';
  }

  /**
   * Helper: format a money amount (in cents) using the active currency.
   */
  formatMoney = function(cents, format = window.Shopify.moneyFormat) {
    if (typeof cents == "string") {
        cents = cents.replace(".", "")
    }
    var value = "";
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || this.money_format;
    function defaultOption(opt, def) {
        return typeof opt == "undefined" ? def : opt
    }
    function formatWithDelimiters(number, precision, thousands, decimal) {
        precision = defaultOption(precision, 2);
        thousands = defaultOption(thousands, ",");
        decimal = defaultOption(decimal, ".");
        if (isNaN(number) || number == null) {
            return 0
        }
        number = (number / 100).toFixed(precision);
        var parts = number.split(".")
          , dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands)
          , cents2 = parts[1] ? decimal + parts[1] : "";
        return dollars + cents2
    }
    switch (formatString.match(placeholderRegex)[1]) {
    case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
    case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
    case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
    case "amount_with_space_separator":
        value = formatWithDelimiters(cents, 2, " ", ",");
        break;
    case "amount_with_period_and_space_separator":
        value = formatWithDelimiters(cents, 2, " ", ".");
        break;
    case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
    case "amount_no_decimals_with_space_separator":
        value = formatWithDelimiters(cents, 0, ".", "");
        break;
    case "amount_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 2, "'", ".");
        break
    }
    return formatString.replace(placeholderRegex, value)
}

  init() {
    // Queries scoped to this component
    this.subscribeCard = this.querySelector('.subscribe-card');
    this.tryOnceCard = this.querySelector('.try-once-card');
    this.deliverySection = this.querySelector('#delivery-frequency-section');
    this.deliverySelect = this.querySelector('#delivery-frequency-select');
    this.finalPriceCompare = this.querySelector('#final-price-compare');
    this.finalPriceCurrent = this.querySelector('#final-price-current');
    this.finalPriceSection = this.querySelector('.final-price-section');
    this.subscriptionDetailsWrapper = this.querySelector('.subscription-details-wrapper');
    this.savingsBadge = this.querySelector('#subscription-savings-badge') || (this.subscribeCard ? this.subscribeCard.querySelector('.savings-badge') : null);

    // Only query radio inputs if parent elements exist
    this.subscribeRadio = this.subscribeCard ? this.subscribeCard.querySelector('.subscription-radio-input') : null;
    this.tryOnceRadio = this.tryOnceCard ? this.tryOnceCard.querySelector('.subscription-radio-input') : null;

    // Get one-time price for savings calculation
    this.oneTimeData = this.querySelector('.onetime-plan-data');
    this.one_time_compare_price = this.oneTimeData ? this.oneTimeData.getAttribute('data-compare-price') : null;

    // Get Liquid variables from data attributes
    this.subscriptionDataElement = this.querySelector('[data-subscription-config]');
    this.variantRequiresSellingPlan = this.subscriptionDataElement ? JSON.parse(this.subscriptionDataElement.getAttribute('data-variant-requires-selling-plan') || 'false') : false;
    this.hasSubscription = this.subscriptionDataElement ? JSON.parse(this.subscriptionDataElement.getAttribute('data-has-subscription') || 'false') : false;
    this.productImageUrl = this.subscriptionDataElement ? this.subscriptionDataElement.getAttribute('data-product-image-url') : '';

    this.servingPriceOverrides = {};
    try {
      const raw = this.subscriptionDataElement ? this.subscriptionDataElement.getAttribute('data-serving-price-overrides') : null;
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          parsed.forEach((entry) => {
            const parts = entry.split(' -- ');
            if (parts.length === 2) {
              const key = parts[0].trim().toLowerCase();
              const val = parseFloat(parts[1].trim());
              if (!isNaN(val)) this.servingPriceOverrides[key] = val;
            }
          });
        }
      }
    } catch (e) {
      this.servingPriceOverrides = {};
    }

    this.bindEvents();
    this.initState();
    this.setupFormSubmitHandler();
    this.setupCartListeners();
  }

  handleKeydown(e, element) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      element.click();
    }
  }

  bindEvents() {
    // Handle card clicks - only add listeners if elements exist
    if (this.subscribeCard) {
      this.subscribeCard.addEventListener('click', () => {
        if (this.variantRequiresSellingPlan) return;

        this.setSubscribeActive();
        this.updatePriceFromSelectedPlan();
        this.triggerSubscriptionChange();
        this.updateDescription();
      });

      this.subscribeCard.addEventListener('keydown', (e) => this.handleKeydown(e, this.subscribeCard));
    }

    if (this.tryOnceCard) {
      this.tryOnceCard.addEventListener('click', () => {
        if (this.variantRequiresSellingPlan) return;

        this.setTryOnceActive();
        this.updatePriceForOneTime();
        this.triggerOneTimeChange();
      });

      this.tryOnceCard.addEventListener('keydown', (e) => this.handleKeydown(e, this.tryOnceCard));
    }

    if (this.deliverySelect) {
      this.deliverySelect.addEventListener('change', () => {
        this.updatePriceFromSelectedPlan();
        this.triggerSubscriptionChange();
        this.updateDescription();
      });
    }
  }

  setSubscribeActive() {
    if (this.subscribeCard) {
      this.subscribeCard.classList.add('active');
      this.subscribeCard.setAttribute('aria-pressed', 'true');
    }
    if (this.tryOnceCard) {
      this.tryOnceCard.classList.remove('active');
      this.tryOnceCard.setAttribute('aria-pressed', 'false');
    }
    if (this.subscribeRadio) this.subscribeRadio.checked = true;
    if (this.tryOnceRadio) this.tryOnceRadio.checked = false;
  }

  setTryOnceActive() {
    if (this.tryOnceCard) {
      this.tryOnceCard.classList.add('active');
      this.tryOnceCard.setAttribute('aria-pressed', 'true');
    }
    if (this.subscribeCard) {
      this.subscribeCard.classList.remove('active');
      this.subscribeCard.setAttribute('aria-pressed', 'false');
    }
    if (this.tryOnceRadio) this.tryOnceRadio.checked = true;
    if (this.subscribeRadio) this.subscribeRadio.checked = false;
  }

  updateDescription() {
    const descriptionContainer = this.querySelector('#subscription-description-container');
    if (descriptionContainer && this.deliverySelect) {
      const selectedOption = this.deliverySelect.options[this.deliverySelect.selectedIndex];
      const planId = selectedOption.getAttribute('data-selling-plan-id');
      const productId = selectedOption.getAttribute('data-product-id');
      this.updateSubscriptionDescription(planId, productId);
    }
  }

  updatePriceFromSelectedPlan() {
    if (!this.deliverySelect) return;

    const selectedOption = this.deliverySelect.options[this.deliverySelect.selectedIndex];
    if (!selectedOption) return;

    const price = selectedOption.getAttribute('data-price');
    const productImage = selectedOption.getAttribute('data-product-image');
    // const productTitle = selectedOption.getAttribute('data-product-title'); // Unused
    // const planId = selectedOption.getAttribute('data-selling-plan-id'); // Unused here
    // const productId = selectedOption.getAttribute('data-product-id'); // Unused here
    const planName = selectedOption.getAttribute('data-selling-plan-name');

    const priceInt = price ? parseInt(price) : 0;

    if (priceInt > 0 && this.finalPriceCurrent) {
      const priceFormatted = this.formatMoney(priceInt, window.Shopify.moneyFormat);
      this.finalPriceCurrent.textContent = priceFormatted;
    }

    if (this.finalPriceCompare && this.oneTimeData) {
      const oneTimePrice = parseInt(this.oneTimeData.getAttribute('data-price')) || 0;

      if (oneTimePrice > 0 && priceInt > 0) {
        let numberOfPeriods = 1;
        if (planName) {
          if (planName.includes('3 months') || planName.includes('3 Month') || planName.includes('Quarterly') || planName.includes('Delivery every 3 Months')) {
            numberOfPeriods = 3;
          } else if (planName.includes('Double Subscription') || planName.includes('Delivery every 30 Days') && planName.includes('Double')) { // Logic check from original
            // Original: includes('Double Subscription') || includes('Delivery every 30 Days') -> This seems broad in original, keeping faithful
            if (planName.includes('Double Subscription')) numberOfPeriods = 2;
            // Wait, re-reading original logic:
            // } else if (planName.includes('Double Subscription') || planName.includes('Delivery every 30 Days')) {
            //   numberOfPeriods = 2; // Double = 2 months (60 days)
            // }
            // But wait, the next else if is:
            // } else if (planName.includes('30 days') || planName.includes('Monthly') || planName.includes('Delivery every 30 Days')) {
            //   numberOfPeriods = 1; // Monthly = 1 month
            // }
            // In original, the first check for 'Delivery every 30 Days' (in the Double block) would match first?
            // Actually, let's stick to the exact original logic order.
            // Re-reading original lines 158-160:
            // } else if (planName.includes('Double Subscription') || planName.includes('Delivery every 30 Days')) {
            //   numberOfPeriods = 2; // Double = 2 months (60 days)
            // (Wait, 'Delivery every 30 Days' usually implies monthly. This might be a bug in original code or very specific naming. I will preserve strictly).
          }
          if (planName.includes('Double Subscription')) { // Refining to likely intent if "Delivery every 30 Days" is ambiguous, but safer to copy original structure if possible.
            // Let's copy original block structure exactly to avoid regression.
            // BUT 'Delivery every 30 Days' appears in BOTH blocks in original.
            // In JS, first match wins. So it would be 2.
          }
        }

        // Exact Original logic reconstruction:
        if (planName) {
          if (planName.includes('3 months') || planName.includes('3 Month') || planName.includes('Quarterly') || planName.includes('Delivery every 3 Months')) {
            numberOfPeriods = 3;
          } else if (planName.includes('Double Subscription')) { // Removed 'Delivery every 30 Days' from here if it conflicts, but original had it?
            // Let's look at original lines 158: `else if (planName.includes('Double Subscription') || planName.includes('Delivery every 30 Days'))`
            // This implies 'Delivery every 30 Days' -> 2 periods? That sounds wrong for a 30 day delivery.
            // However, user said "make sure it's not breaking anything".
            // If I fix it, I might break a specific double setup.
            // But 'Delivery every 30 Days' is standard monthly.
            // I will strictly copy the if/else chain.
            numberOfPeriods = 2;
          } else if (planName.includes('30 days') || planName.includes('Monthly') || planName.includes('Delivery every 30 Days')) {
            numberOfPeriods = 1;
          }
        }
        // Wait, I can't leave 'Delivery every 30 Days' in the 2-period block if it's meant to be 1.
        // Let's assume the original code had a reason or it's a bug I should replicate?
        // Actually, looking at lines 201-204 in original:
        // } else if (planName && planName.includes('Double Subscription')) { days = 60; }
        // } else if (planName && (planName.includes('30 days') || ... || planName.includes('Delivery every 30 Days'))) { days = 30; }
        // THE LOGIC FOR DAYS IS DIFFERENT THAN LOGIC FOR PERIODS IN ORIGINAL?
        // Original Lines 158: `else if (planName.includes('Double Subscription') || planName.includes('Delivery every 30 Days'))` -> numberOfPeriods = 2.
        // Original Lines 201: `else if (planName && planName.includes('Double Subscription'))` -> days = 60.
        // Implies 'Delivery every 30 Days' falls through to next block for days?
        // NO, lines 201 doesn't have 'Delivery every 30 Days'.
        // So for days, 'Delivery every 30 Days' hits line 203 -> days = 30.
        // For periods, 'Delivery every 30 Days' hits line 158 -> periods = 2.
        // This effectively makes 'Delivery every 30 Days' act as a "Double" for compare price calculation (2 * one-time vs sub price).
        // I will preserve this exact behavior.

        if (planName) {
          if (planName.includes('3 months') || planName.includes('3 Month') || planName.includes('Quarterly') || planName.includes('Delivery every 3 Months')) {
            numberOfPeriods = 3;
          } else if (planName.includes('Double Subscription') || planName.includes('Delivery every 30 Days')) {
            numberOfPeriods = 2;
          } else if (planName.includes('30 days') || planName.includes('Monthly')) { // 'Delivery every 30 Days' handled above
            numberOfPeriods = 1;
          }
        }

        const comparePriceInt = oneTimePrice * numberOfPeriods;

        if (comparePriceInt > priceInt) {
          const comparePriceFormatted = this.formatMoney(comparePriceInt);
          this.finalPriceCompare.textContent = comparePriceFormatted;
          this.finalPriceCompare.style.display = 'inline';
        } else {
          this.finalPriceCompare.style.display = 'none';
        }
      } else {
        this.finalPriceCompare.style.display = 'none';
      }
    } else if (this.finalPriceCompare) {
      this.finalPriceCompare.style.display = 'none';
    }

    if (this.subscribeCard) {
      const priceAmount = this.subscribeCard.querySelector('.price-amount');
      if (priceAmount && priceInt > 0) {
        const priceFormatted = this.formatMoney(priceInt);
        priceAmount.textContent = priceFormatted;
      }

      let servingPrice;
      let overrideVal = null;
      if (planName) {
        const planLower = planName.toLowerCase();
        for (const [key, val] of Object.entries(this.servingPriceOverrides)) {
          if (key !== 'onetime' && key !== 'try once' && key !== 'one time' && planLower.includes(key)) {
            overrideVal = val;
            break;
          }
        }
      }
      if (overrideVal !== null) {
        servingPrice = priceInt / overrideVal;
      } else {
        let days = 30;
        if (planName && (planName.includes('3 months') || planName.includes('3 Month') || planName.includes('Quarterly') || planName.includes('Delivery every 3 Months'))) {
          days = 90;
        } else if (planName && planName.includes('Double Subscription')) {
          days = 60;
        }
        servingPrice = priceInt / days;
      }

      const servingFormatted = this.formatMoney(servingPrice);

      const pricePerServing = this.subscribeCard.querySelector('.price-per-serving');
      if (pricePerServing) {
        pricePerServing.textContent = servingFormatted + ' per serving';
      }

      this.updateSavingsBadge(priceInt, planName);
    }

    if (productImage && typeof jQuery !== 'undefined') {
      this.updateImages(productImage);
    }
  }

  updateSavingsBadge(priceInt, planName) {
    if (this.savingsBadge && this.oneTimeData) {
      const oneTimePrice = parseInt(this.oneTimeData.getAttribute('data-price')) || null;

      if (oneTimePrice && priceInt > 0) {
        let numberOfPeriods = 1;
        if (planName) {
          if (planName.includes('3 months') || planName.includes('Quarterly') || planName.includes('Delivery every 3 Months')) {
            numberOfPeriods = 3;
          } else if (planName.includes('Double Subscription') || planName.includes('Delivery every 30 Days')) {
            numberOfPeriods = 2;
          } else if (planName.includes('30 days') || planName.includes('Monthly')) {
            numberOfPeriods = 1;
          }
        }

        const totalOneTimeCost = oneTimePrice * numberOfPeriods;

        if (totalOneTimeCost > priceInt) {
          const savingsAmount = totalOneTimeCost - priceInt;
          const savingsPercent = Math.round((savingsAmount / totalOneTimeCost) * 100);
          if (savingsPercent > 0) {
            this.savingsBadge.textContent = 'Save ' + savingsPercent + '%';
            this.savingsBadge.style.display = 'inline-block';
          } else {
            this.savingsBadge.style.display = 'none';
          }
        } else {
          // Fallback logic
          const comparePrice = null; // Derived from context if available, but original code used variable `comparePrice` which is undefined in scope of `updatePriceFromSelectedPlan`'s `updateSavingsBadge` block in original??
          // Original: `const comparePriceInt = comparePrice ? parseInt(comparePrice) : null;`
          // `comparePrice` was not defined in the original `updatePriceFromSelectedPlan` function scope above the badge block.
          // It might have been relying on a global or it was a bug.
          this.savingsBadge.style.display = 'none';
        }
      } else {
        this.savingsBadge.style.display = 'none';
      }
    }
  }

  updateImages(productImage) {
    jQuery(".thumbnail-list, .product__media-list").find("li:first-child").css({ "opacity": 0 });
    jQuery(".thumbnail-list, .product__media-list").each(function () {
      jQuery(this).find("li:first-child img").attr("src", productImage).attr("srcset", productImage);
      jQuery(".product-media-modal__content").find("img:first-child").attr("src", productImage).attr("srcset", productImage);
      setTimeout(function () {
        jQuery(".thumbnail-list, .product__media-list").find("li:first-child").css({ "opacity": 1 });
      }, 300);
    });
  }

  updateSubscriptionDescription(planId, productId) {
    const descriptionContainer = this.querySelector('#subscription-description-container');
    const descriptionContent = this.querySelector('#subscription-description-content');

    if (!descriptionContainer || !descriptionContent) return;

    const planDescriptions = this.querySelectorAll('.plan-description-data');
    let foundDescription = null;

    for (let desc of planDescriptions) {
      const descPlanId = desc.getAttribute('data-selling-plan-id');
      const descProductId = desc.getAttribute('data-product-id');

      if (descPlanId === planId && descProductId === productId) {
        foundDescription = desc.getAttribute('data-description');
        break;
      }
    }

    if (foundDescription) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = foundDescription;
      descriptionContent.innerHTML = tempDiv.innerHTML;
      descriptionContainer.style.display = 'block';
    } else {
      descriptionContainer.style.display = 'none';
    }
  }

  updatePriceForOneTime() {
    if (this.oneTimeData && this.finalPriceCurrent) {
      const price = this.oneTimeData.getAttribute('data-price');
      const comparePrice = this.oneTimeData.getAttribute('data-compare-price');

      if (price) {
        const priceFormatted = this.formatMoney(parseInt(price, 10) || 0);
        this.finalPriceCurrent.textContent = priceFormatted;
      }

      if (comparePrice && comparePrice > 0 && this.finalPriceCompare) {
        const comparePriceFormatted = this.formatMoney(parseInt(comparePrice, 10) || 0);
        this.finalPriceCompare.textContent = comparePriceFormatted;
        this.finalPriceCompare.style.display = 'inline';
      } else if (this.finalPriceCompare) {
        this.finalPriceCompare.style.display = 'none';
      }
    }
  }

  triggerSubscriptionChange() {
    if (!this.deliverySelect) {
      if (this.variantRequiresSellingPlan && this.hasSubscription) {
        const firstPlan = this.querySelector('.subscription-plan-data');
        if (firstPlan) {
          const planId = firstPlan.getAttribute('data-selling-plan-id');
          const variantId = firstPlan.getAttribute('data-variant-id');
          const productId = firstPlan.getAttribute('data-product-id');
          const addonsIds = firstPlan.getAttribute('data-product-addons') || '';
          this.updateFormFields(planId, variantId, productId, addonsIds);
          return;
        }
      }
      return;
    }

    const selectedOption = this.deliverySelect.options[this.deliverySelect.selectedIndex];
    const planId = selectedOption.getAttribute('data-selling-plan-id');
    const variantId = selectedOption.getAttribute('data-variant-id');
    const productId = selectedOption.getAttribute('data-product-id');
    const addonsIds = selectedOption.getAttribute('data-product-addons') || '';
    const productImage = selectedOption.getAttribute('data-product-image');

    if (this.subscriptionDetailsWrapper) this.subscriptionDetailsWrapper.classList.remove('collapsed');

    this.updateFormFields(planId, variantId, productId, addonsIds, productImage);

    if (productImage && typeof jQuery !== 'undefined') {
      this.updateImages(productImage);
    }

    document.dispatchEvent(new CustomEvent('subscriptionChanged', {
      detail: {
        planId: planId,
        variantId: variantId,
        productId: productId,
        addonsIds: addonsIds
      }
    }));
  }

  triggerOneTimeChange() {
    if (!this.oneTimeData) return;

    const variantId = this.oneTimeData.getAttribute('data-variant-id');
    const productId = this.oneTimeData.getAttribute('data-product-id');
    const addonsIds = this.oneTimeData.getAttribute('data-product-addons') || '';
    // Look for form outside or inside? The updateFormFields looks for document.querySelector.

    // We update form fields to null/empty
    this.updateFormFields('', variantId, productId, addonsIds);

    // Also specific one-time logic
    const descriptionContainer = this.querySelector('#subscription-description-container');
    if (descriptionContainer) {
      descriptionContainer.style.display = 'none';
    }

    const subscriptionDataElement = this.querySelector('[data-subscription-config]');
    const productImage = subscriptionDataElement ? subscriptionDataElement.getAttribute('data-product-image-url') : '';
    if (productImage && typeof jQuery !== 'undefined') {
      this.updateImages(productImage);
    }

    if (this.subscriptionDetailsWrapper) this.subscriptionDetailsWrapper.classList.add('collapsed');

    document.dispatchEvent(new CustomEvent('subscriptionChanged', {
      detail: {
        planId: '',
        variantId: variantId,
        productId: productId,
        addonsIds: addonsIds
      }
    }));
  }

  updateFormFields(planId, variantId, productId, addonsIds, productImage) {
    // Look for form globally or relative to component? 
    // Usually product form is wrapping or adjacent. We look globally as per original script.
    const productForm = document.querySelector('form[data-type="add-to-cart-form"]');

    // But wait, if multiple products (collections), we might grab wrong form.
    // However, the original script operated on document.querySelector too. 
    // Quick View might introduce a new form. We should try to find the Closest form or the form within the modal if we are in one.
    // The Quick View modal usually contains the form.

    let targetForm = productForm;
    const closestForm = this.closest('form');
    if (closestForm) targetForm = closestForm;
    else {
      // Try finding form inside the same product container
      const container = this.closest('.product-custom-template') || this.closest('.product');
      if (container) {
        targetForm = container.querySelector('form[data-type="add-to-cart-form"]') || targetForm;
      }
    }

    if (targetForm) {
      const allSellingPlanInputs = targetForm.querySelectorAll('input[name="selling_plan"]');
      if (allSellingPlanInputs.length > 1) {
        for (let i = 1; i < allSellingPlanInputs.length; i++) {
          allSellingPlanInputs[i].remove();
        }
      }

      const formSellingPlanInput = targetForm.querySelector('input[name="selling_plan"]');
      if (formSellingPlanInput) {
        formSellingPlanInput.value = planId || '';
      }

      const formIdInput = targetForm.querySelector('input[name="id"], .product-variant-id');
      if (formIdInput && variantId) {
        formIdInput.value = variantId;
        formIdInput.disabled = false;
      }
    }

    // Update inputs in the product container (wrapper for add to cart etc)
    const productContainer = this.closest('.product__info-container') || document.querySelector('.product__info-container'); // Fallback to global if not found

    if (productContainer) {
      // logic from original script to update inputs inside product container (separate from form sometimes?)
      const containerSellingPlanInputs = productContainer.querySelectorAll('input[name="selling_plan"]');
      if (containerSellingPlanInputs.length > 1) {
        const inputsArray = Array.from(containerSellingPlanInputs);
        for (let i = 1; i < inputsArray.length; i++) {
          inputsArray[i].remove();
        }
      }

      let idInput = productContainer.querySelector('input[name="id"]');
      if (!idInput) {
        idInput = document.createElement('input');
        idInput.type = 'hidden';
        idInput.name = 'id';
        productContainer.appendChild(idInput);
      }
      idInput.value = variantId || '';

      let productIdInput = productContainer.querySelector('input[name="product-id"]');
      if (!productIdInput) {
        productIdInput = document.createElement('input');
        productIdInput.type = 'hidden';
        productIdInput.name = 'product-id';
        productContainer.appendChild(productIdInput);
      }
      productIdInput.value = productId || '';

      let addonsInput = productContainer.querySelector('input[name="addons_ids"]');
      if (!addonsInput) {
        addonsInput = document.createElement('input');
        addonsInput.type = 'hidden';
        addonsInput.name = 'addons_ids';
        productContainer.appendChild(addonsInput);
      }
      addonsInput.value = addonsIds;
    }
  }

  setupFormSubmitHandler() {
    // This logic ensures if user submits, we enforce the plan selection.
    // We should attach this to the form related to THIS component instance.
    const container = this.closest('.product-custom-template') || this.closest('.product') || document;
    const productForm = container.querySelector('form[data-type="add-to-cart-form"]');

    if (!productForm) return;

    // We need to bind this handler carefully to not duplicate if component re-inits?
    // Actually, component init implies new DOM.
    // But we should use a named handler or check if already attached? 
    // Usually web components are fine to attach to their own form context.

    productForm.addEventListener('submit', (e) => {
      const isSubscribeSelected = this.subscribeCard && this.subscribeCard.classList.contains('active');

      const allSellingPlanInputs = productForm.querySelectorAll('input[name="selling_plan"]');
      if (allSellingPlanInputs.length > 1) {
        for (let i = 1; i < allSellingPlanInputs.length; i++) {
          allSellingPlanInputs[i].remove();
        }
      }

      const sellingPlanInput = productForm.querySelector('input[name="selling_plan"]');
      let planId = null;
      let variantId = null;

      if (this.variantRequiresSellingPlan || isSubscribeSelected) {
        if (this.deliverySelect && this.deliverySelect.options.length > 0) {
          const selectedOption = this.deliverySelect.options[this.deliverySelect.selectedIndex];
          if (selectedOption) {
            planId = selectedOption.getAttribute('data-selling-plan-id');
            variantId = selectedOption.getAttribute('data-variant-id');
          }
        }

        if (!planId) {
          const firstPlan = this.querySelector('.subscription-plan-data');
          if (firstPlan) {
            planId = firstPlan.getAttribute('data-selling-plan-id');
            variantId = firstPlan.getAttribute('data-variant-id');
          }
        }

        if (sellingPlanInput && planId) {
          sellingPlanInput.value = planId;
        }
      } else {
        if (sellingPlanInput) sellingPlanInput.value = '';
        if (this.oneTimeData) variantId = this.oneTimeData.getAttribute('data-variant-id');
      }

      const variantIdInput = productForm.querySelector('input[name="id"], .product-variant-id');
      if (variantIdInput && variantId) {
        variantIdInput.value = variantId;
        variantIdInput.disabled = false;
      }

      if (this.variantRequiresSellingPlan && sellingPlanInput && !sellingPlanInput.value) {
        // Logic to fallback or alert
        const firstPlan = this.querySelector('.subscription-plan-data');
        if (firstPlan) {
          sellingPlanInput.value = firstPlan.getAttribute('data-selling-plan-id');
        } else {
          e.preventDefault();
          e.stopPropagation();
          alert('Please select a subscription plan.');
          return false;
        }
      }
    }, true);
  }

  setupCartListeners() {
    if (typeof subscribe !== 'undefined' && subscribe.PUB_SUB_EVENTS) {
      subscribe(subscribe.PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source === 'product-form') {
          setTimeout(() => this.refreshFormFields(), 100);
        }
      });
    }
    document.addEventListener('cart:updated', () => {
      setTimeout(() => this.refreshFormFields(), 100);
    });
  }

  refreshFormFields() {
    // Re-apply current state to form
    const isSubscribeSelected = this.subscribeCard && this.subscribeCard.classList.contains('active');

    if (isSubscribeSelected && this.deliverySelect) {
      const selectedOption = this.deliverySelect.options[this.deliverySelect.selectedIndex];
      if (selectedOption) {
        const planId = selectedOption.getAttribute('data-selling-plan-id');
        const variantId = selectedOption.getAttribute('data-variant-id');
        const productId = selectedOption.getAttribute('data-product-id');
        const addonsIds = selectedOption.getAttribute('data-product-addons') || '';
        this.updateFormFields(planId, variantId, productId, addonsIds);
      }
    } else if (this.oneTimeData) {
      const variantId = this.oneTimeData.getAttribute('data-variant-id');
      const productId = this.oneTimeData.getAttribute('data-product-id');
      const addonsIds = this.oneTimeData.getAttribute('data-product-addons') || '';
      this.updateFormFields('', variantId, productId, addonsIds);
    }
  }

  initState() {
    if (!this.subscribeCard && !this.tryOnceCard) return;

    if (this.variantRequiresSellingPlan || (this.subscribeCard && this.subscribeCard.classList.contains('active'))) {
      this.setSubscribeActive();
      this.updatePriceFromSelectedPlan();
      this.triggerSubscriptionChange();
      this.updateDescription();
    } else if (this.tryOnceCard && this.tryOnceCard.classList.contains('active')) {
      if (this.subscriptionDetailsWrapper) this.subscriptionDetailsWrapper.classList.add('collapsed');
      this.updatePriceForOneTime();
      this.triggerOneTimeChange();
    } else if (this.subscribeCard && this.hasSubscription) {
      this.setSubscribeActive();
      this.updatePriceFromSelectedPlan();
      this.triggerSubscriptionChange();
      this.updateDescription();
    }
  }
}

customElements.define('custom-subscription', CustomSubscription);